// OWNER: FRONTEND-ADMIN. Numbers page pieces (SPEC §6, §7.8, §15.7): buy flow, import, edit registrations,
// status change with a required note, ledger + daily stats, handset test log, weekly checklist.
import { useState } from 'preact/hooks';
import type { Country, OurNumber } from '../../../src/shared/api';
import { errorMessage } from '../api';
import { ErrorBanner, Modal, fmtDateTime, pct, toast } from '../components/ui';
import { adminApi, type AvailableNumber, type NumberEventKind } from './endpoints';
import { formatPhone, normalizeNanp } from './phone';
import { OPS_TZ, isoToYmd, ymdInTz, ymdToIso } from './dates';
import { SELF_TEST_HINT, attestationLine, statusActions, type StatusAction } from './numbers';
import { apiErrorView, type ApiErrorView } from './errors';
import { ApiErrorBox, Check, ConfirmModal, Field, StatusChip, inputValue, money, useLoad } from './ui';

export const NUMBER_PRICE_USD_MONTH = 1.15; // Twilio local number, US and CA (build-cost.md, pricing "current as of August 2026")

/** Twilio's and TELEPHONY's own words for search, purchase and import failures (a Canadian number may need an address
 *  or bundle; an import can hit 409 number_on_trunk or 502 twilio_error), plus the Twilio code and trunk SID. */
const verbatim = (e: unknown): ApiErrorView => apiErrorView(e);

// ---------------------------------------------------------------- status actions

export { statusActions, type StatusAction } from './numbers';

export function StatusChangeModal(props: { number: OurNumber; action: StatusAction; onDone: (n: OurNumber) => void; onClose: () => void }) {
  const { number: n, action } = props;
  return (
    <ConfirmModal
      title={`${action.label} ${formatPhone(n.e164)}?`}
      body={<div class="stack"><div>{action.help}</div><div class="adm-sub">The note goes into the number ledger (kept as proof we replace numbers, not cycle them).</div></div>}
      confirmLabel={action.label}
      danger={action.danger}
      noteLabel={action.to === 'retired' ? 'Why is it retired?' : 'Note for the ledger'}
      notePlaceholder={action.to === 'flagged' ? 'e.g. Verizon test handset showed “Potential Spam” on 2026-09-24' : ''}
      noteRequired
      onConfirm={async (note) => {
        const next = await adminApi.patchNumber(n.id, { status: action.to, note, ...(action.to === 'retired' ? { retiredReason: note } : {}) });
        toast(`${formatPhone(n.e164)} is now ${action.to}.`, 'ok');
        props.onDone(next);
      }}
      onClose={props.onClose}
    />
  );
}

// ---------------------------------------------------------------- edit registrations

export function EditNumberModal(props: { number: OurNumber; onDone: (n: OurNumber) => void; onClose: () => void }) {
  const n = props.number;
  const [metro, setMetro] = useState(n.metro ?? '');
  const [region, setRegion] = useState(n.region ?? '');
  const [cnamSet, setCnamSet] = useState(n.cnamSet);
  const [fcr, setFcr] = useState(isoToYmd(n.fcrRegisteredAt));
  const [hiya, setHiya] = useState(isoToYmd(n.hiyaRegisteredAt));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const save = async () => {
    if (region && !/^[A-Z]{2}$/.test(region.trim().toUpperCase())) { setError('Region must be a 2-letter state or province code (GA, ON…).'); return; }
    setBusy(true); setError(null);
    try {
      const next = await adminApi.patchNumber(n.id, {
        metro: metro.trim() || null, region: region.trim().toUpperCase() || null, cnamSet,
        fcrRegisteredAt: ymdToIso(fcr), hiyaRegisteredAt: ymdToIso(hiya),
      });
      toast('Number updated.', 'ok');
      props.onDone(next);
      props.onClose();
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  };
  return (
    <Modal title={`Edit ${formatPhone(n.e164)}`} onClose={props.onClose}>
      <div class="stack">
        <div class="adm-grid">
          <Field label="Metro" hint="Atlanta, Toronto…"><input value={metro} onInput={(e) => setMetro(inputValue(e))} /></Field>
          <Field label="Region" hint="State/province code; used to pick caller ID"><input value={region} maxLength={2} onInput={(e) => setRegion(inputValue(e).toUpperCase())} /></Field>
        </div>
        <Check checked={cnamSet} onChange={setCnamSet} disabled={n.country === 'CA'} title={n.country === 'CA' ? 'Twilio CNAM is US-only' : undefined}
          label={n.country === 'CA' ? 'CNAM (US numbers only)' : 'CNAM set (caller name shows “VALISTIX” on landlines)'} />
        <div class="adm-grid">
          <Field label="Free Caller Registry registered on"><input type="date" value={fcr} onInput={(e) => setFcr(inputValue(e))} /></Field>
          <Field label="Hiya registered on"><input type="date" value={hiya} onInput={(e) => setHiya(inputValue(e))} /></Field>
        </div>
        <div class="adm-sub">Register every number, US and Canadian, on Free Caller Registry and Hiya (both free) before its first dial.</div>
        <ErrorBanner error={error} />
        <div class="row" style="justify-content:flex-end">
          <button class="btn" onClick={props.onClose}>Cancel</button>
          <button class="btn btn-primary" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------- ledger

const EVENT_LABEL: Record<NumberEventKind, string> = {
  purchased: 'Purchased', imported: 'Imported', registered: 'Registered', flag_alert: 'Health alert', flagged: 'Flagged',
  remediation: 'Remediation', rested: 'Rested', reactivated: 'Reactivated', retired: 'Retired', test_call: 'Handset test', note: 'Note',
};

const HANDSETS = {
  US: ['AT&T (Hiya)', 'Verizon (TNS)', 'T-Mobile (First Orion)', 'iPhone (iOS 26)', 'Pixel'],
  CA: ['Rogers (Hiya)', 'Bell', 'TELUS', 'iPhone (iOS 26)', 'Pixel'],
} as const;
type HandsetResult = '' | 'clean' | 'labelled' | 'no_ring';

export function LedgerModal(props: { number: OurNumber; mainNumber?: string | null; onClose: () => void; onStatus: (a: StatusAction) => void }) {
  const n = props.number;
  const att = attestationLine(n, ymdInTz(new Date(), OPS_TZ), props.mainNumber ?? null);
  const events = useLoad(() => adminApi.numberEvents(n.id), [n.id]);
  const daily = useLoad(() => adminApi.numberDaily(n.id, 30), [n.id]);
  const [kind, setKind] = useState<'note' | 'remediation' | 'registered'>('note');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);

  const addNote = async () => {
    if (!note.trim()) { setError('Write the note first.'); return; }
    setBusy(true); setError(null);
    try { await adminApi.addNumberEvent(n.id, kind, note.trim()); setNote(''); await events.reload(); } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  };

  const actions = statusActions(n);
  const flag = actions.find((a) => a.to === 'flagged');

  return (
    <Modal title={`Ledger · ${formatPhone(n.e164)}`} onClose={props.onClose} width={900}>
      <div class="stack" style="gap:14px">
        <div class="adm-toolbar">
          <StatusChip status={n.status} />
          {n.health?.alert ? <span class="chip chip-bad" style="white-space:normal">{n.health.alert}</span> : null}
          <span class="grow" />
          {actions.map((a) => (
            <button class={`btn btn-sm${a.danger ? ' btn-danger' : ''}`} title={a.help} onClick={() => props.onStatus(a)}>{a.label}…</button>
          ))}
        </div>
        {n.purpose === 'outbound' ? (
          <div class="row" style="flex-wrap:wrap;gap:8px">
            <span class={`chip ${att.state === 'done' ? 'chip-ok' : att.state === 'warn' ? 'chip-warn' : att.state === 'bad' ? 'chip-bad' : ''}`}>{att.text}</span>
            <span class="adm-sub" style="flex:1;min-width:260px">{att.title}</span>
          </div>
        ) : null}
        <div class="card stack" style="background:var(--paper)">
          <div class="adm-toolbar">
            <b>Add to the ledger</b><span class="grow" />
            <button class="btn btn-sm btn-ink" onClick={() => setTesting(!testing)}>{testing ? 'Close handset test' : 'Log a handset test'}</button>
          </div>
          {testing ? <HandsetTestForm number={n} onLogged={async (labelled) => { setTesting(false); await events.reload(); if (labelled && flag) props.onStatus(flag); }} /> : (
            <div class="row" style="align-items:flex-end;flex-wrap:wrap">
              <Field label="Kind">
                <select value={kind} onChange={(e) => setKind(inputValue(e) as typeof kind)}>
                  <option value="note">Note</option><option value="remediation">Remediation step</option><option value="registered">Registration</option>
                </select>
              </Field>
              <Field label="Note" style="flex:1;min-width:280px"><input value={note} placeholder={kind === 'remediation' ? 'Submitted Hiya mislabel report, ticket #…' : ''} onInput={(e) => setNote(inputValue(e))} /></Field>
              <button class="btn" disabled={busy || !note.trim()} onClick={addNote}>{busy ? 'Adding…' : 'Add'}</button>
            </div>
          )}
          <ErrorBanner error={error} />
        </div>

        <div class="stack">
          <h3>History</h3>
          <ErrorBanner error={events.error} />
          {events.data && events.data.length === 0 ? <div class="muted">No ledger entries yet.</div> : null}
          {events.data && events.data.length ? (
            <div class="adm-table-wrap" style="max-height:260px">
              <table>
                <thead><tr><th>When</th><th>Event</th><th>Note</th><th>By</th></tr></thead>
                <tbody>{[...events.data].sort((a, b) => b.createdAt.localeCompare(a.createdAt)).map((ev) => (
                  <tr><td style="white-space:nowrap">{fmtDateTime(ev.createdAt)}</td>
                    <td><span class={`chip ${ev.kind === 'flagged' || ev.kind === 'flag_alert' ? 'chip-bad' : ev.kind === 'retired' ? '' : 'chip-info'}`}>{EVENT_LABEL[ev.kind] ?? ev.kind}</span></td>
                    <td style="white-space:pre-wrap">{ev.note ?? ''}</td><td>{ev.createdByName ?? (ev.createdBy ? `#${ev.createdBy}` : 'system')}</td></tr>
                ))}</tbody>
              </table>
            </div>
          ) : null}
        </div>

        <div class="stack">
          <h3>Last 30 days</h3>
          <ErrorBanner error={daily.error} />
          {daily.data && daily.data.length === 0 ? <div class="muted">No dials in the last 30 days.</div> : null}
          {daily.data && daily.data.length ? (
            <div class="adm-table-wrap" style="max-height:260px">
              <table>
                <thead><tr><th>Day</th><th class="num">Dials</th><th class="num">Answered</th><th class="num">Connects</th><th class="num">Connect %</th><th class="num">Short (&lt;15 s)</th><th class="num">603</th><th class="num">608</th><th class="num">Failed</th></tr></thead>
                <tbody>{[...daily.data].sort((a, b) => b.day.localeCompare(a.day)).map((d) => (
                  <tr><td class="mono">{d.day}</td><td class="num">{d.dials}</td><td class="num">{d.answered}</td><td class="num">{d.connects}</td>
                    <td class="num">{d.dials ? pct(d.connects / d.dials) : '—'}</td><td class="num">{d.shortCalls}</td>
                    <td class={`num${d.sip603 ? ' chip-bad' : ''}`}>{d.sip603}</td><td class={`num${d.sip608 ? ' chip-bad' : ''}`}>{d.sip608}</td><td class="num">{d.failed}</td></tr>
                ))}</tbody>
              </table>
            </div>
          ) : null}
        </div>
      </div>
    </Modal>
  );
}

function HandsetTestForm(props: { number: OurNumber; onLogged: (labelled: boolean) => void }) {
  const phones = HANDSETS[props.number.country];
  const [results, setResults] = useState<Record<string, { r: HandsetResult; shown: string }>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (p: string, patch: Partial<{ r: HandsetResult; shown: string }>) => {
    setResults((all) => ({ ...all, [p]: { ...(all[p] ?? { r: '' as HandsetResult, shown: '' }), ...patch } }));
  };
  const tested = phones.filter((p) => results[p]?.r);
  const labelled = tested.filter((p) => results[p].r === 'labelled');

  const log = async () => {
    if (tested.length === 0) { setError('Record at least one handset result.'); return; }
    if (labelled.some((p) => !results[p].shown.trim())) { setError('Write what the labelled handset showed (e.g. “Scam Likely”).'); return; }
    const note = 'Handset test: ' + tested.map((p) => {
      const x = results[p];
      return `${p}: ${x.r === 'clean' ? 'clean' : x.r === 'no_ring' ? "didn't ring" : 'LABELLED'}${x.shown.trim() ? ` (“${x.shown.trim()}”)` : ''}`;
    }).join('; ');
    setBusy(true); setError(null);
    try {
      await adminApi.addNumberEvent(props.number.id, 'test_call', note);
      toast(labelled.length ? 'Test logged. A handset showed a label: flag this number.' : 'Handset test logged.', labelled.length ? 'warn' : 'ok');
      props.onLogged(labelled.length > 0);
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  };

  return (
    <div class="stack">
      <div class="adm-sub">Call each test phone from {formatPhone(props.number.e164)} and record what the screen showed (label and caller name).</div>
      <table>
        <thead><tr><th>Handset</th><th>Result</th><th>What it showed</th></tr></thead>
        <tbody>{phones.map((p) => (
          <tr>
            <td>{p}</td>
            <td><select value={results[p]?.r ?? ''} onChange={(e) => set(p, { r: inputValue(e) as HandsetResult })}>
              <option value="">not tested</option><option value="clean">clean</option><option value="labelled">labelled (spam/scam)</option><option value="no_ring">didn't ring</option>
            </select></td>
            <td><input value={results[p]?.shown ?? ''} placeholder="“Spam Risk”, “VALISTIX”, city name…" onInput={(e) => set(p, { shown: inputValue(e) })} style="width:100%" /></td>
          </tr>
        ))}</tbody>
      </table>
      <ErrorBanner error={error} />
      <div><button class="btn btn-ink" disabled={busy} onClick={log}>{busy ? 'Logging…' : 'Log test'}</button></div>
    </div>
  );
}

// ---------------------------------------------------------------- buy + import

export function BuyNumberModal(props: { onBought: (n: OurNumber) => void; onClose: () => void }) {
  const [country, setCountry] = useState<Country>('US');
  const [areaCode, setAreaCode] = useState('');
  const [contains, setContains] = useState('');
  const [results, setResults] = useState<AvailableNumber[] | null>(null);
  const [pick, setPick] = useState<AvailableNumber | null>(null);
  const [metro, setMetro] = useState('');
  const [purpose, setPurpose] = useState<'outbound' | 'main'>('outbound');
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiErrorView | null>(null);
  const say = (message: string) => setError(apiErrorView(new Error(message)));

  const search = async () => {
    if (!/^[2-9]\d{2}$/.test(areaCode)) { say('Enter a 3-digit area code (e.g. 404, 416).'); return; }
    if (contains && !/^[0-9*]{2,10}$/.test(contains)) { say('“Contains” takes 2–10 digits (* matches any digit).'); return; }
    setBusy(true); setError(null); setResults(null); setPick(null);
    try { setResults(await adminApi.searchNumbers({ country, areaCode, contains: contains || undefined })); } catch (e) { setError(verbatim(e)); } finally { setBusy(false); }
  };

  const buy = async () => {
    if (!pick) return;
    setBusy(true); setError(null);
    try {
      const n = await adminApi.buyNumber({ e164: pick.e164, metro: metro.trim() || undefined, purpose, country: pick.country, region: pick.region ?? undefined });
      toast(`Bought ${formatPhone(n.e164)}. It starts ramping at 20 dials/day.`, 'ok');
      props.onBought(n);
      props.onClose();
    } catch (e) {
      setError(verbatim(e));
      setConfirming(false);
    } finally { setBusy(false); }
  };

  return (
    <Modal title="Buy a number" onClose={props.onClose} width={680}>
      <div class="stack">
        {!confirming ? (
          <>
            <div class="adm-sub">Buy local numbers in the metros you actually call. Numbers are never rotated: each lead keeps the same caller ID, and a flagged number is replaced one-for-one with a ledger entry.</div>
            <div class="row" style="align-items:flex-end;flex-wrap:wrap">
              <Field label="Country"><select value={country} onChange={(e) => setCountry(inputValue(e) as Country)}><option value="US">US</option><option value="CA">Canada</option></select></Field>
              <Field label="Area code"><input value={areaCode} maxLength={3} style="width:90px" onInput={(e) => setAreaCode(inputValue(e).replace(/\D/g, ''))} onKeyDown={(e) => { if (e.key === 'Enter') void search(); }} /></Field>
              <Field label="Contains (optional)"><input value={contains} maxLength={10} style="width:120px" placeholder="e.g. 55*5" onInput={(e) => setContains(inputValue(e).replace(/[^0-9*]/g, ''))} /></Field>
              <button class="btn btn-ink" disabled={busy} onClick={search}>{busy && !results ? 'Searching…' : 'Search'}</button>
            </div>
            <ApiErrorBox error={error} />
            {results && results.length === 0 ? <div class="muted">Twilio has no numbers matching that right now. Try a neighbouring area code.</div> : null}
            {results && results.length ? (
              <div class="adm-table-wrap" style="max-height:300px">
                <table>
                  <thead><tr><th /><th>Number</th><th>Locality</th><th>Region</th><th>Address needed</th></tr></thead>
                  <tbody>{results.map((r) => (
                    <tr class="adm-clickable" onClick={() => { setPick(r); if (!metro) setMetro(r.locality ?? ''); }}>
                      <td><input type="radio" name="pick" checked={pick?.e164 === r.e164} /></td>
                      <td class="mono">{formatPhone(r.e164)}</td><td>{r.locality ?? '—'}</td><td>{r.region ?? '—'}</td>
                      <td>{r.addressRequirements && r.addressRequirements !== 'none' ? <span class="chip chip-warn">{r.addressRequirements}</span> : <span class="muted">no</span>}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            ) : null}
            {pick ? (
              <div class="adm-grid">
                <Field label="Metro label"><input value={metro} onInput={(e) => setMetro(inputValue(e))} /></Field>
                <Field label="Purpose" hint="Main = the published callback number; it never dials out.">
                  <select value={purpose} onChange={(e) => setPurpose(inputValue(e) as 'outbound' | 'main')}><option value="outbound">Outbound (caller ID)</option><option value="main">Main callback number</option></select>
                </Field>
              </div>
            ) : null}
            <div class="row" style="justify-content:flex-end">
              <button class="btn" onClick={props.onClose}>Cancel</button>
              <button class="btn btn-primary" disabled={!pick || busy} onClick={() => { setError(null); setConfirming(true); }}>Continue…</button>
            </div>
          </>
        ) : (
          <>
            <div style="font-size:18px">Buy <b class="mono">{pick ? formatPhone(pick.e164) : ''}</b>{pick?.locality ? ` (${pick.locality}${pick.region ? `, ${pick.region}` : ''})` : ''} for <b>{money(NUMBER_PRICE_USD_MONTH)}/month</b>. Purchase?</div>
            <div class="adm-sub">
              Twilio bills {money(NUMBER_PRICE_USD_MONTH)} every month until the number is released (retiring it here keeps it, and keeps billing, so callbacks still ring).
              {purpose === 'outbound' ? ' It starts ramping at 20 dials/day, 40 from day 3, 60 from day 7.' : ' As the main number it never places outbound calls.'}
              {pick && pick.addressRequirements && pick.addressRequirements !== 'none'
                ? ` Twilio requires a ${pick.addressRequirements === 'any' ? '' : `${pick.addressRequirements} `}address on file for this number; if the account doesn't have one, the purchase fails and Twilio's message is shown below.`
                : country === 'CA' ? ' Canadian numbers may need an address or regulatory bundle on the Twilio account; if so Twilio says so below.' : ''}
            </div>
            <div class="adm-muted-box">Before its first dial: register it on Free Caller Registry and Hiya, set CNAM (US), run a handset test, and {SELF_TEST_HINT.toLowerCase()} (STIR/SHAKEN should read A). Log each step in its ledger.</div>
            <ApiErrorBox error={error} />
            <div class="row" style="justify-content:flex-end">
              <button class="btn" disabled={busy} onClick={() => setConfirming(false)}>Back</button>
              <button class="btn btn-primary" disabled={busy} onClick={buy}>{busy ? 'Purchasing…' : `Yes, purchase for ${money(NUMBER_PRICE_USD_MONTH)}/month`}</button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

export function ImportNumberModal(props: { onImported: (n: OurNumber) => void; onClose: () => void }) {
  const [raw, setRaw] = useState('');
  const [purpose, setPurpose] = useState<'outbound' | 'main'>('outbound');
  const [metro, setMetro] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiErrorView | null>(null);
  const parsed = normalizeNanp(raw);
  const go = async () => {
    if (!parsed) { setError(apiErrorView(new Error('Enter a valid US/Canadian number.'))); return; }
    setBusy(true); setError(null);
    try {
      const n = await adminApi.importNumber({ e164: parsed.e164, purpose, metro: metro.trim() || undefined });
      toast(`Imported ${formatPhone(n.e164)}.`, 'ok');
      props.onImported(n);
      props.onClose();
    } catch (e) { setError(verbatim(e)); } finally { setBusy(false); }
  };
  return (
    <Modal title="Import a number already on the Twilio account" onClose={props.onClose}>
      <div class="stack">
        <div class="adm-sub">No purchase: this adds a number the account already owns and points its voice webhook at the dialer (inbound callbacks and the voicemail box). It starts ramping like a new number.</div>
        <Field label="Number" hint={parsed ? parsed.e164 : raw ? 'Not a valid number yet' : ''}><input value={raw} placeholder="(416) 555-0123" onInput={(e) => setRaw(inputValue(e))} /></Field>
        <div class="adm-grid">
          <Field label="Metro label"><input value={metro} onInput={(e) => setMetro(inputValue(e))} /></Field>
          <Field label="Purpose">
            <select value={purpose} onChange={(e) => setPurpose(inputValue(e) as 'outbound' | 'main')}><option value="outbound">Outbound (caller ID)</option><option value="main">Main callback number</option></select>
          </Field>
        </div>
        <ApiErrorBox error={error} />
        <div class="row" style="justify-content:flex-end">
          <button class="btn" onClick={props.onClose}>Cancel</button>
          <button class="btn btn-primary" disabled={busy || !parsed} onClick={go}>{busy ? 'Importing…' : 'Import'}</button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------- weekly checklist

export function HandsetChecklist() {
  return (
    <details class="card">
      <summary style="cursor:pointer"><b>Weekly number check (handset test + Hiya)</b> <span class="adm-sub">— full remediation steps: RUNBOOK.md in the dialer folder</span></summary>
      <ol style="margin:10px 0 0;padding-left:20px;line-height:1.7">
        <li><b>Hiya console</b>: check spam status and the report card for every number.</li>
        <li><b>Handset panel</b>: test every new number before its first dial, every number with a health alert, and a quarter of the pool each week. US: AT&amp;T, Verizon, T-Mobile. Canada: Rogers, Bell, TELUS. Plus an iPhone (iOS 26) and a Pixel. Log each test in the number's ledger (“Log a handset test”).</li>
        <li><b>CNAM</b> (US): call a landline or business VoIP line and confirm the caller name shows VALISTIX, not a city.</li>
        <li><b>Any label</b> → Flag the number here (it stops dialling), then remediate: Hiya console mislabel report; Free Caller Registry (T-Mobile/First Orion, Verizon/TNS); Verizon voicespamfeedback.com. Log each step as “Remediation”.</li>
        <li><b>Replace, don't cycle</b>: buy a replacement only after two failed remediation cycles, one-for-one, and retire the old number with the reason. Never release it (callbacks).</li>
        <li><b>SIP 603/608</b> in the table means a carrier rejected calls: treat it as a label and flag the number.</li>
      </ol>
    </details>
  );
}
